import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import GridFilterPanel from '../../../commons/filters/GridFilterPanel.jsx';
import * as Utils from '../../../commons/utils/Utils.js';
import { ANIOS } from '../../../commons/constants/anios.js';
import {
	MOVILES_INMOVILIZADOS_FILTROS_STORAGE_KEY,
	buildMovilesInmovilizadosFilterFields,
	getMovilesInmovilizadosFilterDefaults,
} from './movilesInmovilizadosFilterFields.js';

/**
 * Filtros del reporte de Móviles Inmovilizados.
 *
 * VEC-3696: este componente pasó de tener el panel escrito a mano (601 líneas, 13 `<Select>`
 * de MUI repetidos, su propio estado y su propia persistencia con switch) a ser sólo la capa
 * que ALIMENTA a <GridFilterPanel />: carga las opciones y las normaliza a `{value, label}`.
 * El estado, la persistencia y la serialización son del panel compartido; ver
 * docs/design-system/grid-filter-panel.md.
 *
 * Los nombres de campo y los tipos de control se mantuvieron, así el query que arma la grilla
 * no cambió. Pasar los desplegables a múltiples es la fase 2.
 */

const OPTIONS_INIT = {
	tipoDeTicket: [],
	movilEstado: [],
	base: [],
	region: [],
	subregion: [],
	servicio: [],
	gerenciador: [],
	abono: [],
	funcion: [],
	marca: [],
	modelo: [],
	anio: [],
	tipoVehiculo: [],
};

const MovilesInmovilizadosGridFiltros = (props) => {
	const intl = useIntl();
	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	const fields = buildMovilesInmovilizadosFilterFields();

	// Los tres combos que no salen de un endpoint: estaban hardcodeados en el panel viejo.
	const tiposDeTicket = [
		{ value: 'CORRECTIVO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_correctivo', defaultMessage: 'CORRECTIVO' }) },
		{ value: 'PREVENTIVO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_preventivo', defaultMessage: 'PREVENTIVO' }) },
		{ value: 'VENCIMIENTO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_vencimiento', defaultMessage: 'VENCIMIENTO' }) },
	];

	const estadosDeVehiculo = [
		{ value: 'OPERATIVO', label: 'OPERATIVO' },
		{ value: 'BACKUP', label: 'BACKUP' },
	];

	// El panel viejo mostraba "Abono SI" / "Abono NO" en el renderValue, pero "SI"/"NO" en las
	// opciones del desplegable. Se conserva lo que se leía AL ELEGIR, que es la lista.
	const abonos = [
		{ value: 'SI', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_abono_si', defaultMessage: 'SI' }) },
		{ value: 'NO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_abono_no', defaultMessage: 'NO' }) },
	];

	useEffect(() => {
		setOptionsLoading(true);

		Promise.all([
			Utils.getData('/bases/select-filtered-by-user/base/'),
			Utils.getData('/servicios/select'),
			Utils.getData('/regiones/select'),
			Utils.getData('/subregiones/select-all'),
			Utils.getData('/gerenciadores/select-filtered-by-user'),
			Utils.getData('/marcas/select'),
			Utils.getData('/modelo-tipos/select'),
			Utils.getData('/funciones/select'),
		])
			.then(([bases, servicios, regiones, subregiones, gerenciadores, marcas, tiposVehiculo, funciones]) => {
				setOptions((prev) =>
					Object.assign({}, prev, {
						base: bases,
						servicio: servicios,
						region: regiones,
						subregion: subregiones,
						gerenciador: gerenciadores,
						marca: marcas,
						tipoVehiculo: tiposVehiculo,
						funcion: funciones,
						anio: ANIOS,
						tipoDeTicket: tiposDeTicket,
						movilEstado: estadosDeVehiculo,
						abono: abonos,
					})
				);
				setOptionsLoading(false);
			})
			.catch(() => setOptionsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * *** `modelo` DEPENDE DE `marca`: es el único caso de desplegable dependiente del patrón. ***
	 *
	 * Sus opciones salen de `/modelos/marca/{id}/select`, así que hay que recargarlas apenas
	 * cambia la marca —no al apretar "Buscar"—. Para eso existe `onFieldChange`, la prop
	 * aditiva que se le agregó al panel en esta card: avisa la edición en el momento.
	 *
	 * El panel viejo hacía lo mismo con un `useEffect` sobre `props.filters.marca`, que acá no
	 * sirve porque el estado de edición vive adentro del panel.
	 *
	 * Desde la fase 2 `marca` es MÚLTIPLE, así que los modelos son la unión de los de cada
	 * marca elegida: un pedido por marca, en paralelo. El endpoint es por marca y no acepta una
	 * lista; con dos o tres marcas elegidas son dos o tres pedidos, que es lo que hay.
	 */
	const modelosDelEstadoInicial = useRef(false);

	/**
	 * Corrección que el wrapper le impone al panel cuando el estado quedó inconsistente.
	 *
	 * Se usa `overrideMerge` para que se aplique SOBRE lo que el usuario tiene cargado y no
	 * sobre los defaults: acá sólo se toca `modelo`, el resto de los filtros queda como está.
	 *
	 * Y va con `overrideDraftOnly` porque es una corrección del BORRADOR: no dispara `onApply`
	 * ni toca los filtros aplicados/persistidos. Sin eso —lo marcó el review del PR 2387—,
	 * sacar una marca guardaba en localStorage el borrador entero, con esa edición y cualquier
	 * otra pendiente, aunque el usuario nunca hubiera apretado Buscar: al recargar, eso
	 * arrancaba como query activo.
	 */
	const [correccion, setCorreccion] = useState(null);

	/**
	 * *** LOS MODELOS SE CACHEAN POR MARCA, NO MERGEADOS EN UNA SOLA LISTA. ***
	 *
	 * Guardarlos por marca es lo que permite que la poda de `modelo` (ver abajo) sea
	 * SINCRÓNICA: sacar una marca no necesita ir a la red, las opciones que siguen siendo
	 * válidas se recalculan de acá. Mientras la poda esperaba la respuesta del endpoint había
	 * tres agujeros —los marcó el review del PR 2387— por los que el modelo huérfano
	 * sobrevivía: si el pedido fallaba, si el usuario apretaba Buscar antes de que volviera, o
	 * si editaba el filtro en el medio y la respuesta tardía le pisaba lo nuevo.
	 *
	 * De paso, sumar una marca pide sólo la marca nueva: lo ya cargado no se vuelve a pedir.
	 */
	const modelosPorMarca = useRef({});

	/** Marcas elegidas ahora. Lo que se muestra y lo que se poda sale SIEMPRE de acá. */
	const marcasVigentes = useRef([]);

	/** Marcas con el pedido en vuelo, para no pedir dos veces la misma. */
	const enVuelo = useRef({});

	const idsDe = (opciones) =>
		(Array.isArray(opciones) ? opciones : [])
			.filter((o) => o && o.value !== undefined && o.value !== null)
			.map((o) => o.value);

	const opcionesDeMarcas = (marcas) => {
		const porValue = {};
		marcas.forEach((id) => {
			(modelosPorMarca.current[id] || []).forEach((m) => {
				porValue[m.value] = m;
			});
		});
		return Object.values(porValue);
	};

	/**
	 * Último estado del panel que el wrapper conoce. Lo escribe `onFieldChange` —o sea, toda
	 * edición del usuario— y lo necesita la poda diferida de abajo: cuando los modelos de una
	 * marca llegan tarde, hay que podar contra lo que el usuario tiene AHORA y no contra el
	 * snapshot del momento en que se pidieron.
	 *
	 * Queda en `null` mientras nadie editó nada y tras un "Restaurar filtros": en los dos casos
	 * no hay nada que corregir, y podar contra un borrador viejo repondría chips ya borrados.
	 */
	const borrador = useRef(null);

	const aplicarCorreccion = (modelos) => {
		if (borrador.current) borrador.current = Object.assign({}, borrador.current, { modelo: modelos });
		setCorreccion({ modelo: modelos });
	};

	/**
	 * *** AL SACAR UNA MARCA HAY QUE PODAR SUS MODELOS YA ELEGIDOS. ***
	 *
	 * Si no, el modelo queda en el filtro sin su marca y la consulta sale con una combinación
	 * imposible —`marca=5&modelo=34`— que devuelve CERO filas sin ningún aviso: para el usuario
	 * parece que no hay vehículos paralizados. Lo encontró QA (VEC-3696).
	 *
	 * *** LA VALIDEZ DEPENDE SÓLO DE LAS MARCAS QUE QUEDAN. *** Un modelo vale si está entre
	 * los de alguna marca todavía elegida; de qué marca vino no importa. Por eso lo único que
	 * puede impedir decidir es que falte cargar la lista de alguna marca que QUEDA: ahí se
	 * difiere y se rehace cuando llega (ver `cargarModelos`). No se difiere por la marca que se
	 * sacó —su respuesta puede no llegar nunca— ni se conserva un modelo "por las dudas" cuando
	 * ya se lo puede evaluar (review del PR 2387).
	 *
	 * Diferir, y no borrar, es a propósito: los chips de modelo los repone localStorage antes de
	 * que lleguen los modelos de las marcas guardadas, y darlos por huérfanos en esa ventana
	 * borraría en silencio un filtro válido que el usuario había guardado.
	 */
	const podarModelosElegidos = (elegidos, marcas) => {
		if (!elegidos || elegidos.length === 0) return;
		if (marcas.some((id) => !modelosPorMarca.current[id])) return;

		const validos = {};
		marcas.forEach((id) => {
			modelosPorMarca.current[id].forEach((m) => { validos[String(m.value)] = true; });
		});

		const sobreviven = elegidos.filter((o) => o && validos[String(o.value)]);

		if (sobreviven.length !== elegidos.length) aplicarCorreccion(sobreviven);
	};

	/**
	 * Repone el desplegable y rehace la poda con lo que hay en el cache AHORA.
	 *
	 * Todo sale de `marcasVigentes` y del borrador vigente, nunca de las variables del pedido
	 * que disparó la llamada: por eso una respuesta que llega tarde no puede pisar la lista de
	 * lo que está elegido, y no hace falta ningún guardia de vigencia.
	 */
	const refrescarModelos = () => {
		setOptions((prev) => Object.assign({}, prev, { modelo: opcionesDeMarcas(marcasVigentes.current) }));
		if (borrador.current) podarModelosElegidos(borrador.current.modelo, idsDe(borrador.current.marca));
	};

	/**
	 * Pide los modelos de UNA marca y los cachea apenas llegan.
	 *
	 * *** CADA MARCA SE CACHEA SOLA, SIN ESPERAR A LAS OTRAS. *** Con un `Promise.all` para
	 * todas, una marca lenta dejaba el desplegable vacío aunque las otras ya hubieran
	 * respondido, y la poda diferida no podía usar lo que sí había llegado (review del PR 2387).
	 *
	 * `enVuelo` evita pedir dos veces la misma marca mientras su pedido viaja: sin eso, cada
	 * edición de marcas que conserve una marca todavía sin cachear la volvía a pedir. Se libera
	 * al terminar, así un pedido que falló se puede reintentar.
	 */
	const pedirModelosDe = (id) => {
		if (modelosPorMarca.current[id] || enVuelo.current[id]) return;
		enVuelo.current[id] = true;

		Utils.getData(`/modelos/marca/${id}/select`)
			.then((modelos) => {
				modelosPorMarca.current[id] = modelos || [];
			})
			.catch(() => {})
			.then(() => {
				delete enVuelo.current[id];
				refrescarModelos();
			});
	};

	/**
	 * Deja el desplegable de modelos con los de `marcas`, pidiendo sólo las que falten.
	 *
	 * @param modelosElegidos si viene, se podan los que dejaron de pertenecer a alguna marca.
	 *                        Sólo se pasa desde la edición del usuario: al montar, el estado
	 *                        persistido se deja como está.
	 */
	const cargarModelos = (marcas, modelosElegidos) => {
		marcasVigentes.current = marcas;

		setOptions((prev) => Object.assign({}, prev, { modelo: opcionesDeMarcas(marcas) }));
		podarModelosElegidos(modelosElegidos, marcas);

		// El endpoint es por marca y no acepta una lista: un pedido por marca, en paralelo.
		marcas.forEach(pedirModelosDe);
	};

	const handleFieldChange = (name, valor, estado) => {
		borrador.current = estado || null;
		if (name !== 'marca') return;

		cargarModelos(idsDe(Array.isArray(valor) ? valor : [valor]), (estado && estado.modelo) || []);
	};

	/**
	 * *** `applyOnMount` NO DISPARA `onFieldChange`, SÓLO `onApply`. ***
	 *
	 * Al reingresar con una marca persistida, los chips se ven pero el desplegable de modelos
	 * queda VACÍO: nadie pidió sus opciones, porque el usuario no editó nada. El resultado es
	 * que no puede elegir un modelo hasta volver a tocar la marca.
	 *
	 * Se resuelve leyendo el query que el panel entrega al montar —ahí viene `marca` ya
	 * serializada, "3,5"— y cargando los modelos una sola vez. No hace falta que el wrapper
	 * sepa nada de la persistencia: el panel ya le está diciendo con qué arrancó.
	 */
	const handleApply = (query) => {
		if (!modelosDelEstadoInicial.current) {
			modelosDelEstadoInicial.current = true;
			const marcas = String(query.marca || '').split(',').filter((v) => v !== '');
			if (marcas.length > 0) cargarModelos(marcas);
		}

		// *** UN PEDIDO QUE FALLÓ SE REINTENTA ACÁ. ***
		// Si el de una marca que sigue elegida falla, su lista nunca se cachea y una poda que
		// quedó diferida por ella no se completa más: el modelo huérfano sigue viajando en cada
		// búsqueda. `pedirModelosDe` no hace nada para las marcas ya cacheadas o en vuelo, así
		// que esto sólo reintenta lo que falta, y el "indefinidamente" pasa a ser "hasta el
		// próximo Buscar" sin agregarle al usuario ningún botón ni ninguna espera
		// (review del PR 2387).
		marcasVigentes.current.forEach(pedirModelosDe);

		if (props.onApply) props.onApply(query);
	};

	return (
		<GridFilterPanel
			open={props.dropdownActive}
			fields={fields}
			options={options}
			defaults={getMovilesInmovilizadosFilterDefaults()}
			storageKey={MOVILES_INMOVILIZADOS_FILTROS_STORAGE_KEY}
			loading={optionsLoading}
			resetSignal={props.filterResetInputs}
			onResetSignalHandled={() => {
				// El reset vacía las marcas, así que el desplegable de modelos tiene que quedar
				// vacío también: si no, siguen ofreciéndose los modelos de las marcas anteriores y
				// se puede elegir uno sin ninguna marca y mandarlo con Buscar. Vaciar
				// `marcasVigentes` además deja sin efecto a cualquier pedido en vuelo, que al
				// volver repone el desplegable desde acá (review del PR 2387).
				borrador.current = null;
				marcasVigentes.current = [];
				setOptions((prev) => Object.assign({}, prev, { modelo: [] }));
				props.setFilterResetInputs(false);
			}}
			onFieldChange={handleFieldChange}
			overrideState={correccion}
			overrideSignal={correccion}
			overrideMerge
			overrideDraftOnly
			onOverrideSignalHandled={() => setCorreccion(null)}
			onApply={handleApply}
			applyOnMount
		/>
	);
};

export default MovilesInmovilizadosGridFiltros;
